// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React, { useState, useMemo } from "react";

const TEAM = [
  { name: "Jordan Lutz", initials: "JL" },
  { name: "Ryan Miskulin", initials: "RM" },
  { name: "Taylor Schenher", initials: "TS" },
  { name: "Des Bachynski", initials: "DB" },
  { name: "Lucas Mason", initials: "LM" },
  { name: "Lee Mehling", initials: "LMe" },
  { name: "Clorice Robertson", initials: "CR" },
  { name: "Tyson Becker", initials: "TB" },
  { name: "Tyler Hodgson", initials: "TH" },
];

const COLUMNS = ["Backlog", "To Do", "In Progress", "Review", "Done"];
const PRIORITIES = ["P0", "P1", "P2", "P3"];
const TAGS = ["development", "sales", "marketing", "legal", "finance", "operations", "urgent"];

const INITIAL_TASKS = [
  { id:1, title:"Implement real-time notifications system", assignee:"Jordan Lutz", priority:"P0", status:"In Progress", due:"2026-02-10", tags:["development","urgent"], project:"Platform",
    description:"Build WebSocket-based notification system for real-time updates across all apps.",
    subtasks:[{text:"Set up WebSocket server",done:true},{text:"Create notification schema",done:true},{text:"Build frontend listener",done:false},{text:"Test across browsers",done:false}],
    comments:[{author:"Ryan Miskulin",text:"Priority feature for Q1 launch",time:"2 days ago"},{author:"Tyler Hodgson",text:"Performance benchmarks look good",time:"1 day ago"}],
    activity:["Created by Jordan Lutz - Feb 1","Moved to In Progress - Feb 3","Subtask completed: Set up WebSocket server - Feb 5","Comment added by Ryan - Feb 6"] },
  { id:2, title:"Q1 Revenue Forecast Model", assignee:"Taylor Schenher", priority:"P0", status:"Review", due:"2026-02-08", tags:["finance","urgent"], project:"Finance",
    description:"Complete the Q1 revenue forecast model with projections for all product lines.",
    subtasks:[{text:"Gather Q4 actuals",done:true},{text:"Build projection model",done:true},{text:"Peer review",done:false},{text:"Present to board",done:false}],
    comments:[{author:"Ryan Miskulin",text:"Need this before board meeting Friday",time:"3 days ago"},{author:"Clorice Robertson",text:"Q4 numbers reconciled and ready",time:"2 days ago"}],
    activity:["Created by Taylor Schenher - Jan 28","Moved to In Progress - Feb 1","Moved to Review - Feb 7"] },
  { id:3, title:"Canstorr partnership agreement draft", assignee:"Tyson Becker", priority:"P1", status:"In Progress", due:"2026-02-12", tags:["legal","sales"], project:"Partnerships",
    description:"Draft the partnership agreement for Canstorr commodities expansion into western Canada.",
    subtasks:[{text:"Outline key terms",done:true},{text:"Legal review",done:false},{text:"Internal approval",done:false},{text:"Send to Canstorr legal",done:false}],
    comments:[{author:"Lucas Mason",text:"VP of Canstorr wants this by mid-Feb",time:"1 day ago"}],
    activity:["Created by Tyson Becker - Feb 3","Moved to In Progress - Feb 5"] },
  { id:4, title:"Sales deck refresh for Q1 campaigns", assignee:"Lucas Mason", priority:"P1", status:"To Do", due:"2026-02-15", tags:["sales","marketing"], project:"Sales",
    description:"Update all sales presentation decks with new branding, case studies, and Q4 results.",
    subtasks:[{text:"Collect case studies",done:false},{text:"Update slide templates",done:false},{text:"Add Q4 metrics",done:false}],
    comments:[],activity:["Created by Lucas Mason - Feb 6"] },
  { id:5, title:"Mining sector compliance audit", assignee:"Lee Mehling", priority:"P1", status:"To Do", due:"2026-02-18", tags:["operations","legal"], project:"Mining",
    description:"Annual compliance audit for all mining sector operations and partnerships.",
    subtasks:[{text:"Review safety protocols",done:false},{text:"Inspect documentation",done:false},{text:"Generate report",done:false},{text:"File with regulators",done:false}],
    comments:[],activity:["Created by Lee Mehling - Feb 4"] },
  { id:6, title:"Health platform beta testing", assignee:"Des Bachynski", priority:"P1", status:"In Progress", due:"2026-02-14", tags:["development","operations"], project:"Health",
    description:"Coordinate beta testing for the health platform with 3 pilot clinics.",
    subtasks:[{text:"Select pilot clinics",done:true},{text:"Deploy beta build",done:true},{text:"Collect feedback",done:false},{text:"Fix critical bugs",done:false}],
    comments:[{author:"Jordan Lutz",text:"Beta build is deployed. No critical bugs yet.",time:"6 hrs ago"}],
    activity:["Created - Feb 1","Moved to In Progress - Feb 5","Beta deployed - Feb 7"] },
  { id:7, title:"Accounts receivable reconciliation", assignee:"Clorice Robertson", priority:"P2", status:"Done", due:"2026-02-06", tags:["finance"], project:"Finance",
    description:"Monthly AR reconciliation for January.",subtasks:[{text:"Pull AR report",done:true},{text:"Cross-reference payments",done:true},{text:"Flag discrepancies",done:true},{text:"Generate summary",done:true}],
    comments:[{author:"Taylor Schenher",text:"All clear, great work",time:"1 day ago"}],activity:["Created - Jan 30","Completed - Feb 6"] },
  { id:8, title:"Overwatch monitoring dashboard enhancements", assignee:"Tyler Hodgson", priority:"P2", status:"In Progress", due:"2026-02-13", tags:["development"], project:"Platform",
    description:"Add new KPI widgets and alert thresholds to the Overwatch dashboard.",subtasks:[{text:"Design new widgets",done:true},{text:"Implement backend APIs",done:false},{text:"Frontend integration",done:false}],
    comments:[],activity:["Created - Feb 3","In Progress - Feb 5"] },
  { id:9, title:"Client onboarding documentation", assignee:"Lucas Mason", priority:"P2", status:"Backlog", due:"2026-02-20", tags:["sales","marketing"], project:"Sales",
    description:"Create comprehensive client onboarding guides and welcome packets.",subtasks:[{text:"Draft welcome guide",done:false},{text:"Create video walkthrough",done:false},{text:"Design templates",done:false}],
    comments:[],activity:["Created - Feb 6"] },
  { id:10, title:"Database performance optimization", assignee:"Jordan Lutz", priority:"P1", status:"Backlog", due:"2026-02-22", tags:["development","urgent"], project:"Platform",
    description:"Optimize slow queries and add indexing for high-traffic tables.",subtasks:[{text:"Profile slow queries",done:false},{text:"Add indexes",done:false},{text:"Load test",done:false}],
    comments:[],activity:["Created - Feb 7"] },
  { id:11, title:"Commodities market report Q1", assignee:"Tyson Becker", priority:"P2", status:"To Do", due:"2026-02-16", tags:["operations"], project:"Commodities",
    description:"Quarterly market analysis for North American commodities.",subtasks:[{text:"Gather market data",done:false},{text:"Analyze trends",done:false},{text:"Write report",done:false}],
    comments:[],activity:["Created - Feb 5"] },
  { id:12, title:"Employee benefits renewal review", assignee:"Clorice Robertson", priority:"P2", status:"To Do", due:"2026-02-28", tags:["finance","operations"], project:"HR",
    description:"Review and negotiate employee benefits package renewal.",subtasks:[{text:"Compare plans",done:false},{text:"Get team input",done:false},{text:"Negotiate rates",done:false}],
    comments:[],activity:["Created - Feb 4"] },
  { id:13, title:"Fleet management API integration", assignee:"Jordan Lutz", priority:"P1", status:"Backlog", due:"2026-02-25", tags:["development"], project:"Fleet",
    description:"Integrate fleet tracking API with central dashboard.",subtasks:[{text:"API documentation review",done:false},{text:"Build adapter",done:false},{text:"Test endpoints",done:false},{text:"Deploy",done:false}],
    comments:[],activity:["Created - Feb 7"] },
  { id:14, title:"Insurance broker lead follow-up campaign", assignee:"Lucas Mason", priority:"P2", status:"In Progress", due:"2026-02-11", tags:["sales","marketing"], project:"Insurance",
    description:"Execute email drip campaign for insurance broker leads from January webinar.",subtasks:[{text:"Segment leads",done:true},{text:"Draft email sequence",done:true},{text:"Set up automation",done:false},{text:"Monitor metrics",done:false}],
    comments:[{author:"Ryan Miskulin",text:"Good open rates so far",time:"12 hrs ago"}],activity:["Created - Feb 2","In Progress - Feb 4"] },
  { id:15, title:"Board presentation Q1 strategy", assignee:"Ryan Miskulin", priority:"P0", status:"Review", due:"2026-02-09", tags:["urgent","operations"], project:"Executive",
    description:"Prepare Q1 board presentation with strategic priorities and financial outlook.",subtasks:[{text:"Compile department updates",done:true},{text:"Financial slides",done:true},{text:"Strategy roadmap",done:true},{text:"Final review",done:false}],
    comments:[{author:"Taylor Schenher",text:"Financial slides are ready",time:"4 hrs ago"}],activity:["Created - Feb 1","In Progress - Feb 3","Review - Feb 7"] },
  { id:16, title:"Mining site visit report - Western Mining", assignee:"Lee Mehling", priority:"P2", status:"Done", due:"2026-02-05", tags:["operations"], project:"Mining",
    description:"Complete site visit report from Western Mining HQ tour.",subtasks:[{text:"Compile photos",done:true},{text:"Write findings",done:true},{text:"Submit to management",done:true}],
    comments:[],activity:["Created - Feb 3","Completed - Feb 5"] },
  { id:17, title:"Health compliance documentation update", assignee:"Des Bachynski", priority:"P2", status:"To Do", due:"2026-02-19", tags:["operations","legal"], project:"Health",
    description:"Update health compliance documentation to meet 2026 regulatory changes.",subtasks:[{text:"Review new regs",done:false},{text:"Update docs",done:false},{text:"Legal review",done:false}],
    comments:[],activity:["Created - Feb 6"] },
  { id:18, title:"Security penetration testing", assignee:"Tyler Hodgson", priority:"P0", status:"To Do", due:"2026-02-17", tags:["development","urgent"], project:"Security",
    description:"Schedule and execute quarterly security pen test across all production systems.",subtasks:[{text:"Select testing firm",done:false},{text:"Define scope",done:false},{text:"Execute tests",done:false},{text:"Review findings",done:false}],
    comments:[],activity:["Created - Feb 7"] },
];
export default function TaskBoardPage() {
  const [tasks, setTasks] = useState(INITIAL_TASKS);
  const [selectedTask, setSelectedTask] = useState(null);
  const [filterAssignee, setFilterAssignee] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [filterProject, setFilterProject] = useState("");
  const [filterTag, setFilterTag] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTask, setNewTask] = useState({ title:"", assignee:"", priority:"P2", tags:[], project:"", description:"" });
  const [newComment, setNewComment] = useState("");

  const projects = useMemo(() => [...new Set(tasks.map(t => t.project))], [tasks]);

  const filteredTasks = useMemo(() => {
    return tasks.filter(t => {
      if (filterAssignee && t.assignee !== filterAssignee) return false;
      if (filterPriority && t.priority !== filterPriority) return false;
      if (filterProject && t.project !== filterProject) return false;
      if (filterTag && !t.tags.includes(filterTag)) return false;
      return true;
    });
  }, [tasks, filterAssignee, filterPriority, filterProject, filterTag]);

  const moveTask = (taskId, newStatus) => {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
  };

  const addTask = () => {
    if (!newTask.title) return;
    const task = { ...newTask, id: Date.now(), status: "Backlog", due: "2026-02-28", subtasks: [], comments: [], activity: ["Created - just now"], tags: newTask.tags.length ? newTask.tags : ["operations"] };
    setTasks(prev => [...prev, task]);
    setNewTask({ title:"", assignee:"", priority:"P2", tags:[], project:"", description:"" });
    setShowAddModal(false);
  };

  const toggleSubtask = (taskId, idx) => {
    setTasks(prev => prev.map(t => {
      if (t.id !== taskId) return t;
      const subtasks = [...t.subtasks];
      subtasks[idx] = { ...subtasks[idx], done: !subtasks[idx].done };
      return { ...t, subtasks };
    }));
  };

  const addComment = (taskId) => {
    if (!newComment.trim()) return;
    setTasks(prev => prev.map(t => {
      if (t.id !== taskId) return t;
      return { ...t, comments: [...t.comments, { author: "You", text: newComment, time: "just now" }] };
    }));
    setNewComment("");
  };

  const isOverdue = (due) => new Date(due) < new Date() && due !== "Done";

  return (
    <div>
      <div className="page-header">
        <div><h1>Task Board</h1><p>{filteredTasks.length} tasks across {COLUMNS.length} columns</p></div>
        <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>+ New Task</button>
      </div>

      <div className="kanban-controls">
        <select value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)}>
          <option value="">All Assignees</option>
          {TEAM.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
        </select>
        <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
          <option value="">All Priorities</option>
          {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={filterProject} onChange={e => setFilterProject(e.target.value)}>
          <option value="">All Projects</option>
          {projects.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={filterTag} onChange={e => setFilterTag(e.target.value)}>
          <option value="">All Tags</option>
          {TAGS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        {(filterAssignee || filterPriority || filterProject || filterTag) && (
          <button className="btn btn-secondary btn-sm" onClick={() => { setFilterAssignee(""); setFilterPriority(""); setFilterProject(""); setFilterTag(""); }}>Clear Filters</button>
        )}
      </div>

      <div className="kanban-board">
        {COLUMNS.map(col => {
          const colTasks = filteredTasks.filter(t => t.status === col);
          return (
            <div key={col} className="kanban-column">
              <div className="kanban-column-header">
                <h3>{col}</h3>
                <span className="count">{colTasks.length}</span>
              </div>
              <div className="kanban-cards">
                {colTasks.map(task => (
                  <div key={task.id} className="kanban-card" onClick={() => setSelectedTask(task)}>
                    <div className="kanban-card-title">{task.title}</div>
                    <div className="kanban-card-meta">
                      <span className={"priority-tag priority-" + task.priority.toLowerCase()}>{task.priority}</span>
                      <span className="assignee-tag">{TEAM.find(t => t.name === task.assignee)?.initials || "?"}</span>
                      <span className={"due-tag" + (isOverdue(task.due) ? " overdue" : "")}>{task.due}</span>
                    </div>
                    <div style={{display:"flex",gap:4,marginTop:6,flexWrap:"wrap"}}>
                      {task.tags.map(tag => <span key={tag} className="tag-pill">{tag}</span>)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Task Detail Modal */}
      {selectedTask && (
        <div className="modal-overlay" onClick={() => setSelectedTask(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedTask.title}</h2>
              <button className="modal-close" onClick={() => setSelectedTask(null)}>X</button>
            </div>
            <div className="modal-body">
              <div style={{display:"flex",gap:12,marginBottom:16,flexWrap:"wrap"}}>
                <span className={"priority-tag priority-"+selectedTask.priority.toLowerCase()}>{selectedTask.priority}</span>
                <span style={{fontSize:13,color:"#94a3b8"}}>Assigned to: <strong style={{color:"#10b981"}}>{selectedTask.assignee}</strong></span>
                <span style={{fontSize:13,color:"#94a3b8"}}>Due: <strong style={{color:isOverdue(selectedTask.due)?"#ef4444":"#e2e8f0"}}>{selectedTask.due}</strong></span>
                <span style={{fontSize:13,color:"#94a3b8"}}>Project: <strong style={{color:"#e2e8f0"}}>{selectedTask.project}</strong></span>
              </div>

              <div style={{marginBottom:16}}>
                <h4 style={{fontSize:13,fontWeight:700,color:"#94a3b8",marginBottom:6}}>Description</h4>
                <p style={{fontSize:13,color:"#e2e8f0",lineHeight:1.6}}>{selectedTask.description}</p>
              </div>

              <div style={{marginBottom:16}}>
                <h4 style={{fontSize:13,fontWeight:700,color:"#94a3b8",marginBottom:6}}>Move To</h4>
                <div style={{display:"flex",gap:8}}>
                  {COLUMNS.filter(c => c !== selectedTask.status).map(c => (
                    <button key={c} className="btn btn-secondary btn-sm" onClick={() => { moveTask(selectedTask.id, c); setSelectedTask({...selectedTask, status:c}); }}>{c}</button>
                  ))}
                </div>
              </div>

              <div style={{marginBottom:16}}>
                <h4 style={{fontSize:13,fontWeight:700,color:"#94a3b8",marginBottom:6}}>Tags</h4>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {selectedTask.tags.map(tag => <span key={tag} className="tag-pill">{tag}</span>)}
                </div>
              </div>

              <div style={{marginBottom:16}}>
                <h4 style={{fontSize:13,fontWeight:700,color:"#94a3b8",marginBottom:8}}>Subtasks ({selectedTask.subtasks.filter(s=>s.done).length}/{selectedTask.subtasks.length})</h4>
                <div className="progress-bar" style={{marginBottom:8}}>
                  <div className="progress-bar-fill" style={{width:(selectedTask.subtasks.length?selectedTask.subtasks.filter(s=>s.done).length/selectedTask.subtasks.length*100:0)+"%"}} />
                </div>
                <div className="checklist">
                  {selectedTask.subtasks.map((st, idx) => (
                    <div key={idx} className={"checklist-item"+(st.done?" done":"")}>
                      <input type="checkbox" checked={st.done} onChange={() => toggleSubtask(selectedTask.id, idx)} />
                      <span>{st.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{marginBottom:16}}>
                <h4 style={{fontSize:13,fontWeight:700,color:"#94a3b8",marginBottom:8}}>Comments ({selectedTask.comments.length})</h4>
                <div className="comments-list">
                  {selectedTask.comments.map((c, idx) => (
                    <div key={idx} className="comment">
                      <div className="comment-avatar">{c.author.split(" ").map(n=>n[0]).join("")}</div>
                      <div className="comment-body">
                        <div className="comment-author">{c.author}</div>
                        <div className="comment-text">{c.text}</div>
                        <div className="comment-time">{c.time}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="comment-input">
                  <input value={newComment} onChange={e=>setNewComment(e.target.value)} placeholder="Add a comment..." onKeyDown={e=>{if(e.key==="Enter")addComment(selectedTask.id);}} />
                  <button className="btn btn-primary btn-sm" onClick={()=>addComment(selectedTask.id)}>Send</button>
                </div>
              </div>

              <div>
                <h4 style={{fontSize:13,fontWeight:700,color:"#94a3b8",marginBottom:8}}>Activity Log</h4>
                <div className="timeline">
                  {selectedTask.activity.map((a, idx) => (
                    <div key={idx} className="timeline-item">
                      <div className="timeline-content">{a}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Task Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Task</h2>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>X</button>
            </div>
            <div className="modal-body">
              <div className="form-field">
                <label>Task Title</label>
                <input value={newTask.title} onChange={e => setNewTask({...newTask, title: e.target.value})} placeholder="Enter task title" />
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label>Assignee</label>
                  <select value={newTask.assignee} onChange={e => setNewTask({...newTask, assignee: e.target.value})}>
                    <option value="">Select assignee</option>
                    {TEAM.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
                  </select>
                </div>
                <div className="form-field">
                  <label>Priority</label>
                  <select value={newTask.priority} onChange={e => setNewTask({...newTask, priority: e.target.value})}>
                    {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-field">
                <label>Project</label>
                <select value={newTask.project} onChange={e => setNewTask({...newTask, project: e.target.value})}>
                  <option value="">Select project</option>
                  {projects.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div className="form-field">
                <label>Description</label>
                <textarea value={newTask.description} onChange={e => setNewTask({...newTask, description: e.target.value})} placeholder="Describe the task..." />
              </div>
              <div className="form-field">
                <label>Tags</label>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {TAGS.map(tag => (
                    <button key={tag} className={"tag-pill"} style={{cursor:"pointer",background:newTask.tags.includes(tag)?"#10b981":"#334155",color:newTask.tags.includes(tag)?"white":"#94a3b8"}}
                      onClick={() => setNewTask({...newTask, tags: newTask.tags.includes(tag) ? newTask.tags.filter(t=>t!==tag) : [...newTask.tags, tag]})}>{tag}</button>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={addTask}>Create Task</button>
            </div>
          </div>
        </div>
      )}

      {/* Task Summary Stats */}
      <div className="grid-4" style={{marginTop:24}}>
        {COLUMNS.map(col => {
          const count = filteredTasks.filter(t => t.status === col).length;
          return (
            <div key={col} className="stat-card" style={{textAlign:"center"}}>
              <div className="stat-label">{col}</div>
              <div className="stat-value">{count}</div>
            </div>
          );
        })}
        <div className="stat-card" style={{textAlign:"center"}}>
          <div className="stat-label">Total Tasks</div>
          <div className="stat-value">{filteredTasks.length}</div>
        </div>
      </div>

      {/* Priority Distribution */}
      <div className="card" style={{marginTop:16}}>
        <div className="card-header"><h3>Priority Distribution</h3></div>
        <div className="chart-bar-container">
          {PRIORITIES.map(pri => {
            const count = filteredTasks.filter(t => t.priority === pri).length;
            const pct = filteredTasks.length ? (count / filteredTasks.length * 100) : 0;
            const colors = {P0:"red",P1:"orange",P2:"",P3:"blue"};
            return (
              <div key={pri} className="chart-bar-row">
                <span className="chart-bar-label">{pri}</span>
                <div className="chart-bar-track">
                  <div className={"chart-bar-fill "+(colors[pri]||"")} style={{width:pct+"%"}}>{count}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}